import { type TFunction } from 'i18next';

import { request } from '../helpers/authorizedRequests';

export type VoucherStatus =
  | 'VALID'
  | 'INVALID'
  | 'REDEEMED'
  | 'NO_BALANCE'
  | 'EXPIRED'
  | 'NOT_STARTED'
  | 'CURRENCY_MISMATCH'
  | 'REQUIRES_AUTHENTICATION';

export type VoucherType = {
  voucher_code: string;
  expiry_date?: string;
  balance: number;
  currency: string;
  voucher_status: VoucherStatus;
  voucher_provider: string;
};

export function getVoucher(
  voucherCode: string,
  offeringId: string,
  currency: string,
): Promise<VoucherType> {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CURRENCY': currency,
    },
  };
  return request(
    `ecommerce/vouchers/${voucherCode}?productOfferingId=${offeringId}`,
    options,
  );
}

export function getAdvertiserIdFromVoucher(
  voucherCode: string,
): Promise<string> {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
  return request(`ecommerce/vouchers/${voucherCode}/advertiser_id`, options);
}

export const currencyQueryOptions = async (currency: string) => {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };
  const data = await request(`resources/resources/currency`, options);
  const matchedCurrency = data.find(
    (item: { id: string }) => item.id === currency,
  );

  return matchedCurrency.alias;
};

export const getVoucherErrorMessage = async ({
  t,
  status,
  currency,
}: {
  t: TFunction;
  status: VoucherStatus;
  currency: string;
}) => {
  switch (status) {
    case 'NO_BALANCE':
      return t(
        'voucher_payment.error_no_credit',
        'The voucher code you have used has no credit. Please top up this voucher or try another code.',
      );
    case 'INVALID':
      return t(
        'voucher_payment.error_not_found',
        'Voucher code not found. Please check your code and try again.',
      );
    case 'CURRENCY_MISMATCH':
      if (!currency) {
        return t(
          'voucher_payment.error_no_currency',
          'Currency information from voucher is missing. Please contact the issuer of the voucher.',
        );
      }
      return t(
        'voucher_payment.error_currency_mismatch',
        'This voucher is for purchases with {{currency}}. Please refer to your voucher for more information.',
        { currency: await currencyQueryOptions(currency) },
      );
    case 'EXPIRED':
      return t(
        'voucher_payment.error_expired',
        'The voucher code you have used is expired. Please use another code.',
      );
    case 'NOT_STARTED':
      return t(
        'voucher_payment.error_not_started',
        'This voucher is not yet valid. Please try again after the start date of this promotion.',
      );
    case 'REDEEMED':
      return t(
        'voucher_payment.error_redeemed',
        'This voucher has already been redeemed.',
      );
    case 'REQUIRES_AUTHENTICATION':
      return t(
        'voucher_payment.error_requires_authentication',
        'You must be logged in to use this voucher.',
      );
    default:
      return t(
        'voucher_payment.error_generic',
        'Something went wrong and we were unable to apply the voucher - please check your 12-digit code and try again.',
      );
  }
};
